import {Redirect, Route, Switch, useParams, useRouteMatch} from 'react-router-dom';
import {Box, SxProps} from '@mui/material';
import {useAuth} from '../../../../../../contexts/auth/context';
import {useOffice} from '../../../../../../contexts/office/context';
import {GenericCollapsibleSidePanel} from '../../../../../../components/generic-collapsible-side-panel/generic-collapsible-side-panel';
import {DigitalInclusionSidePanelComponents} from '../../../../../../components/generic-collapsible-side-panel/digital-inclusion-side-panel-components';
import {DeviceTabs} from '../../../../../../components/device-tabs/device-tabs';
import ApplicationsProfessionalCaregiverOfficeScreen from '../../applications/_root/applications.professional-caregiver.screen';
import ApplicationProfessionalCaregiverOfficeScreen from '../../applications/edit/application.professional-caregiver.screen';
import EntityPhotosProfessionalCaregiverOfficeScreen from '../../entity-photos/_root/entity-photos.professional-caregiver.screen';
import EntityPhotoProfessionalCaregiverOfficeScreen from '../../entity-photos/edit/entity-photo.professional-caregiver.screen';
import UserCardsProfessionalCaregiverOfficeScreen from '../../user-cards/_root/user-cards.professional-caregiver.screen';
import UserCardProfessionalCaregiverOfficeScreen from '../../user-cards/edit/user-card.professional-caregiver.screen';
import DevicesProfessionalCaregiverOfficeScreen from '../../devices/_root/devices.professional-caregiver.screen';
import PlaylistsProfessionalCaregiverOfficeScreen from '../../playlists/_root/playlists.professional-caregiver.screen';
import PlaylistProfessionalCaregiverOfficeScreen from '../../playlists/edit/playlist.professional-caregiver.screen';
import EntityServicesProfessionalCaregiverOfficeScreen from '../../entity-services/_root/entity-services.professional-caregiver.screen';
import EntityServiceProfessionalCaregiverOfficeScreen from '../../entity-services/edit/entity-service.professional-caregiver.screen';
import {CaregiverMode, ScreenPaths} from '../../../../../../enums';
import {RolePermissionType} from '../../../../../../backend/enums/role-permission-type';
import AccessRequiredRoute from '../../../../../../components/access-required-route';
import {useMemo, useState} from 'react';

const DigitalInclusionProfessionalCaregiverOfficeScreen = () => {
  const {path, url} = useRouteMatch();
  const {hasWritePermission, hasReadPermission, state: authState} = useAuth();
  const officeContext = useOffice();
  let {entityId}: {entityId: string} = useParams();
  const outterWrapperSx: SxProps = {
    display: 'flex',
    flexDirection: 'row',
  };
  const innerWrapperSx: SxProps = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  };
  const boxWrapperSx: SxProps = {
    background: '#FFFFFF',
    width: '100%',
    padding: {xs: '5px 16px 25px 16px', md: '10px 34px 40px 34px'},
    borderRadius: '10px',
  };

  const showProfessionalCaregiverDigitalInclusionOptions = useMemo(
    () =>
      hasReadPermission(RolePermissionType.DigitalInclusion_Applications) ||
      hasReadPermission(RolePermissionType.DigitalInclusion_Media) ||
      hasReadPermission(RolePermissionType.DigitalInclusion_Cards) ||
      hasReadPermission(RolePermissionType.DigitalInclusion_Devices) ||
      hasReadPermission(RolePermissionType.DigitalInclusion_Services) ||
      hasReadPermission(RolePermissionType.DigitalInclusion_Playlists),
    [hasReadPermission]
  );

  const [
    canWriteApplications,
    canWriteMedia,
    canWriteUserCards,
    canWriteDevices,
    canWriteServices,
    canWritePlaylists,
  ] = useMemo(
    () => [
      hasWritePermission(RolePermissionType.DigitalInclusion_Applications),
      hasWritePermission(RolePermissionType.DigitalInclusion_Media),
      hasWritePermission(RolePermissionType.DigitalInclusion_Cards),
      hasWritePermission(RolePermissionType.DigitalInclusion_Devices),
      hasWritePermission(RolePermissionType.DigitalInclusion_Services),
      hasWritePermission(RolePermissionType.DigitalInclusion_Playlists),
    ],
    [hasWritePermission]
  );
  const [isSubCategoryExpanded, setIsSubCategoryExpanded] = useState<
    {menu: string; id: string} | undefined
  >({menu: '', id: ''});

  return (
    <Box sx={outterWrapperSx}>
      {showProfessionalCaregiverDigitalInclusionOptions && (
        <GenericCollapsibleSidePanel>
          <DigitalInclusionSidePanelComponents
            showExpanded={!officeContext.state.sideNavigationDrawerCollapsed}
            isSubCategoryExpanded={isSubCategoryExpanded}
          />
        </GenericCollapsibleSidePanel>
      )}
      <Box sx={innerWrapperSx}>
        <Switch>
          {/* Applications */}
          <AccessRequiredRoute
            exact
            entityId={entityId}
            access="digital_inclusion"
            path={`${path}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Applications}`}
            requiredPermissions={[
              {
                type: RolePermissionType.DigitalInclusion_Applications,
                canRead: true, // users must be able to have read permissions of `RolePermissionType.DigitalInclusion_Applications`
              },
            ]}
            render={() => {
              setIsSubCategoryExpanded(undefined);
              return (
                <ApplicationsProfessionalCaregiverOfficeScreen canWrite={canWriteApplications} />
              );
            }}
          />
          <AccessRequiredRoute
            path={`${path}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Application}`}
            entityId={entityId}
            access="digital_inclusion"
            requiredPermissions={[
              {
                type: RolePermissionType.DigitalInclusion_Applications,
                canRead: true, // users must be able to have read permissions of `RolePermissionType.DigitalInclusion_Applications`
              },
            ]}
            render={() => {
              return (
                <ApplicationProfessionalCaregiverOfficeScreen
                  onApplicationChange={(id) => setIsSubCategoryExpanded({menu: 'applications', id})}
                  canWrite={canWriteApplications}
                />
              );
            }}
          />

          {/* Photos */}
          <AccessRequiredRoute
            exact
            path={`${path}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Photos}`}
            entityId={entityId}
            access="digital_inclusion"
            requiredPermissions={[
              {
                type: RolePermissionType.DigitalInclusion_Media,
                canRead: true, // users must be able to have read permissions of `DigitalInclusion_Media`
              },
            ]}
            render={() => {
              setIsSubCategoryExpanded(undefined);
              return <EntityPhotosProfessionalCaregiverOfficeScreen canWrite={canWriteMedia} />;
            }}
          />

          <AccessRequiredRoute
            path={`${path}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Photo}`}
            entityId={entityId}
            access="digital_inclusion"
            requiredPermissions={[
              {
                type: RolePermissionType.DigitalInclusion_Media,
                canRead: true, // users must be able to have read permissions of `DigitalInclusion_Media` to access the page in read only mode
              },
            ]}
            render={() => {
              setIsSubCategoryExpanded(undefined);
              return <EntityPhotoProfessionalCaregiverOfficeScreen canWrite={canWriteMedia} />;
            }}
          />

          {/* User cards */}
          <AccessRequiredRoute
            exact
            path={`${path}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Cards}`}
            entityId={entityId}
            access="digital_inclusion"
            requiredPermissions={[
              {
                type: RolePermissionType.DigitalInclusion_Cards,
                canRead: true, // users must be able to have read permissions of `DigitalInclusion_Cards` to access the page in read only mode
              },
            ]}
            render={() => {
              setIsSubCategoryExpanded(undefined);
              return <UserCardsProfessionalCaregiverOfficeScreen />;
            }}
          />
          <AccessRequiredRoute
            path={`${path}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Card}`}
            entityId={entityId}
            access="digital_inclusion"
            requiredPermissions={[
              {
                type: RolePermissionType.DigitalInclusion_Cards,
                canRead: true, // users must be able to have read permissions of `DigitalInclusion_Cards` to access the page in read only mode
              },
            ]}
            render={() => {
              setIsSubCategoryExpanded(undefined);
              return <UserCardProfessionalCaregiverOfficeScreen canWrite={canWriteUserCards} />;
            }}
          />

          {/* Devices */}
          <AccessRequiredRoute
            exact
            path={`${path}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Devices}`}
            entityId={entityId}
            access="digital_inclusion"
            requiredPermissions={[
              {
                type: RolePermissionType.DigitalInclusion_Devices,
                canRead: true, // users must be able to have read permissions of `RolePermissionType.DigitalInclusion_Devices`
              },
            ]}
            render={() => {
              setIsSubCategoryExpanded(undefined);
              return <DevicesProfessionalCaregiverOfficeScreen />;
            }}
          />

          <AccessRequiredRoute
            path={`${path}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Device}`}
            entityId={entityId}
            access="digital_inclusion"
            requiredPermissions={[
              {
                type: RolePermissionType.DigitalInclusion_Devices,
                canRead: true, // users must be able to have read permissions of `RolePermissionType.DigitalInclusion_Devices`
              },
            ]}
            render={() => {
              setIsSubCategoryExpanded(undefined);
              return (
                <Box sx={boxWrapperSx}>
                  <DeviceTabs
                    caregiverMode={CaregiverMode.Professional}
                    canWrite={canWriteDevices}
                  />
                </Box>
              );
            }}
          />

          {/* Services */}
          {authState.session?.selectedAffiliation?.entityServicesAvailable === true && (
            <AccessRequiredRoute
              exact
              path={`${path}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Services}`}
              entityId={entityId}
              access="digital_inclusion"
              requiredPermissions={[
                {
                  type: RolePermissionType.DigitalInclusion_Services,
                  canRead: true, // users must be able to have read permissions of `DigitalInclusion_Services` to access the page of edit mode in read only
                },
              ]}
              render={() => {
                setIsSubCategoryExpanded(undefined);
                return (
                  <EntityServicesProfessionalCaregiverOfficeScreen canWrite={canWriteServices} />
                );
              }}
            />
          )}

          {authState.session?.selectedAffiliation?.entityServicesAvailable === true && (
            <AccessRequiredRoute
              path={`${path}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Service}`}
              entityId={entityId}
              access="digital_inclusion"
              requiredPermissions={[
                {
                  type: RolePermissionType.DigitalInclusion_Services,
                  canRead: true, // users must be able to have read permissions of `DigitalInclusion_Services` to access the page of edit mode in read only
                },
              ]}
              render={() => {
                setIsSubCategoryExpanded(undefined);
                return (
                  <EntityServiceProfessionalCaregiverOfficeScreen canWrite={canWriteServices} />
                );
              }}
            />
          )}

          {/* Playlists */}
          <AccessRequiredRoute
            exact
            path={`${path}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Playlists}`}
            entityId={entityId}
            access="digital_inclusion"
            requiredPermissions={[
              {
                type: RolePermissionType.DigitalInclusion_Playlists,
                canRead: true, // users must be able to have read permissions of `DigitalInclusion_Playlists` to access the page in read only mode
              },
            ]}
            render={() => {
              setIsSubCategoryExpanded(undefined);
              return <PlaylistsProfessionalCaregiverOfficeScreen canWrite={canWritePlaylists} />;
            }}
          />
          <AccessRequiredRoute
            path={`${path}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_PlaylistItems}`}
            entityId={entityId}
            access="digital_inclusion"
            requiredPermissions={[
              {
                type: RolePermissionType.DigitalInclusion_Playlists,
                canRead: true, // users must be able to have read permissions of `DigitalInclusion_Playlists` to access the page in read only mode
              },
            ]}
            render={() => {
              return (
                <PlaylistProfessionalCaregiverOfficeScreen
                  canWrite={canWritePlaylists}
                  onPlaylistChange={(id) => setIsSubCategoryExpanded({menu: 'playlists', id})}
                />
              );
            }}
          />
          {/*  Default route */}
          <Route path={path}>
            <Redirect
              to={`${url}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Applications}`}
            />
          </Route>
        </Switch>
      </Box>
    </Box>
  );
};

export default DigitalInclusionProfessionalCaregiverOfficeScreen;
